import { buildConversionOptions } from './conversionMapper';
import { resolveFieldType } from './inputTypeMapper';
import { getFeatureById, getCustomFieldById, getConversionById } from './lookup';
import { normalizeGenderSpecific } from '../utils/normalizeGenderSpecific';

const socialCodeMap = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  twitter: 'Twitter',
  x: 'Twitter',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  imdb: 'IMDB',
  linkedin: 'LinkedIn',
  pinterest: 'Pinterest',
  behance: 'Behance',
  vimeo: 'Vimeo',
  models: 'ModelsCom',
  modelscom: 'ModelsCom',
  website: 'Personal',
  personal: 'Personal',
};

const normalizeOptions = (options) => {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }

  const seenValues = new Set();

  return options
    .map((option) => {
      if (option === null || option === undefined) {
        return null;
      }

      if (typeof option === 'string' || typeof option === 'number') {
        return {
          value: String(option),
          label: String(option),
        };
      }

      const idLike =
        option.ID ?? option.Id ?? option.id ?? option.Key ?? option.key ?? option.Code ?? option.code;
      const hasStableId =
        idLike !== null && idLike !== undefined && String(idLike).trim() !== '';

      let value;
      if (hasStableId) {
        value = String(idLike);
      } else if (option.value !== null && option.value !== undefined && String(option.value).trim() !== '') {
        value = String(option.value);
      } else if (option.Value !== null && option.Value !== undefined && String(option.Value).trim() !== '') {
        value = String(option.Value);
      } else {
        return null;
      }

      const label = String(
        option.label ??
          option.Label ??
          option.Name ??
          option.name ??
          (hasStableId ? option.Value : null) ??
          option.Value ??
          option.value ??
          value
      );

      return {
        value,
        label,
      };
    })
    .filter(Boolean)
    .filter((option) => {
      if (seenValues.has(option.value)) {
        return false;
      }

      seenValues.add(option.value);
      return true;
    });
};

const getAdditionalProps = (field) => {
  return field?.AdditionalProps && typeof field.AdditionalProps === 'object' ? field.AdditionalProps : {};
};

const resolveSocialCode = (field, additionalProps) => {
  const rawCode =
    additionalProps?.SocialCode ??
    additionalProps?.socialCode ??
    field?.SocialCode ??
    field?.socialCode ??
    field?.Code;

  const normalizedCode = String(rawCode || '').trim();
  if (!normalizedCode) {
    return null;
  }

  return socialCodeMap[normalizedCode.toLowerCase()] || normalizedCode;
};

// Only the form field decides whether it is gender specific. The feature
// definition carries a GenderSpecificName of its own, deliberately ignored here.
const resolveGenderSpecific = (additionalProps) =>
  normalizeGenderSpecific(additionalProps?.GenderSpecific ?? additionalProps?.genderSpecific);

const resolveOptions = ({ field, additionalProps, featureInfo, customFieldInfo }) => {
  if (featureInfo?.Choices) {
    return normalizeOptions(featureInfo.Choices);
  }

  if (customFieldInfo?.Options) {
    return normalizeOptions(customFieldInfo.Options);
  }

  if (field?.Choices) {
    return normalizeOptions(field.Choices);
  }

  if (field?.Options) {
    return normalizeOptions(field.Options);
  }

  if (additionalProps?.Choices) {
    return normalizeOptions(additionalProps.Choices);
  }

  if (additionalProps?.Options) {
    return normalizeOptions(additionalProps.Options);
  }

  return [];
};

const resolveConversion = ({ field, additionalProps, featureInfo, customFieldInfo, conversions }) => {
  const directConversionSources = [
    field?.conversion,
    field?.Conversion,
    additionalProps?.conversion,
    additionalProps?.Conversion,
  ];

  const directSource = directConversionSources.find(Boolean);
  if (directSource) {
    return buildConversionOptions(directSource);
  }

  if (field?.ConversionValues || field?.AvailableMetrics || field?.metrics || field?.options) {
    return buildConversionOptions(field);
  }

  const conversionId =
    additionalProps?.ConversionID ??
    additionalProps?.conversionId ??
    field?.ConversionID ??
    field?.conversionId ??
    featureInfo?.ConversionID ??
    featureInfo?.conversionId ??
    customFieldInfo?.ConversionID ??
    customFieldInfo?.conversionId;

  if (!conversionId) {
    return null;
  }

  const conversion = getConversionById(conversionId, conversions);
  if (!conversion) {
    return null;
  }

  return buildConversionOptions(conversion);
};

export const transformField = (field, features, customFields, conversions) => {
  const { Label, Code, Description, DataType, Section, Required } = field;
  const additionalProps = getAdditionalProps(field);
  const type = resolveFieldType(DataType);

  if (type === 'Photo') {
    return null;
  }

  const featureInfo = Section === 'Feature' ? getFeatureById(Code, features) : null;
  const customFieldInfo = Section === 'CustomField' ? getCustomFieldById(Code, customFields) : null;
  const genderSpecific = resolveGenderSpecific(additionalProps);

  const fieldConfig = {
    name: Code,
    label: Label,
    type,
    required: Required,
    description: Description,
    section: Section,
  };

  if (type === 'Social') {
    fieldConfig.socialCode = resolveSocialCode(field, additionalProps);
  }

  if (type === 'Choice' || type === 'MultipleChoice') {
    fieldConfig.options = resolveOptions({
      field,
      additionalProps,
      featureInfo,
      customFieldInfo,
    });

    if (type === 'MultipleChoice' && fieldConfig.options.length > 0) {
      fieldConfig.options = [...fieldConfig.options].sort((a, b) => a.label.localeCompare(b.label));
    }
  }

  if (genderSpecific) {
    fieldConfig.genderSpecific = genderSpecific;
  }

  if (Section === 'Feature' && featureInfo) {
    fieldConfig.featureName = featureInfo.Name ?? featureInfo.name;
  }

  if (type === 'Conversion') {
    fieldConfig.conversion = resolveConversion({
      field,
      additionalProps,
      featureInfo,
      customFieldInfo,
      conversions,
    });
  }

  if (type === 'BigText') {
    fieldConfig.validation = {
      maxLength: 2000,
    };
  }

  if (Object.keys(additionalProps).length > 0) {
    fieldConfig.additionalProps = additionalProps;
  }

  return fieldConfig;
};
